import { Component, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { PlaceService } from '@app/modules/model/svcplace';
import { QrCode } from '@app/modules/model/qrcode';

import { alertService } from '@app/modules/common/alert';
import { languageService, languageSupport } from '@app/modules/common/language';
import { deviceService } from '@app/modules/sockets';
import { syncService } from '@app/modules/sync';
import { dataService } from '@app/modules/data';
import { viewService } from '@app/modules/view';

import { UiRaspPIDevices } from '../devices/ui-devices';

/************************************/
/* USER COMPONENT                   */
/************************************/

@Component({
    selector: 'ui-rasppi-view',
    templateUrl: 'ui-view.html',
    styleUrls: [
      'ui-view.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiRaspPIView extends languageSupport  implements OnInit, OnChanges, OnDestroy { 
    @Input('service') _service: PlaceService = null;
    @Input('active') _active = false;

    OnViewChanged(){
        this.change.markForCheck();
    }

    get _place(){
        return this._service?.place;
    }

    /*****************************/
    /* SERVICE DATA FORM         */
    /*****************************/

    private _serviceForm = null;   
    get ServiceForm(){
        if (this._serviceForm == null){
            this.LoadServiceForm();
        }
        return this._serviceForm;
    }

    private _checking = null;       // avoid testing multiple times
    private checkAvailableService(control: FormControl): any {
        if (control.value == this._service.name){
            return null;    // no changes
        }

        // check the name is not being used in this place
        for (let _service of this._place.services){
            if (_service.IsValid && (control.value == _service.name) && (_service != this._service)){
                return { available: true };
            }
        }

        // check the name is not being used in other place
        if (this._checking){
            clearTimeout(this._checking);

            this._checking = setTimeout(() => {
                this.checkAvailableService(control);
            }, 500);

            return;
        }
        else {
            let _service = control.value;
            if (_service){
                this._checking = setTimeout(() => { /* empty */ }, 0);

                this.devsvc.Available(this.sync, this._place.objref, _service).then(
                data => {
                    this._checking = null;
                    if (!data && control.valid){
                        return { available: true };  
                    }
                });        
            }
        }

        return null;
    }

    private get _POSQrCodes(){
        let _qrcodes: Array <QrCode> = [];
        for(let _qrcode of this._service.tables){
            if ((_qrcode.IsValid)){
                _qrcodes.push(_qrcode);
            }
        }

        _qrcodes.sort((a, b) => {
            return Number(a.number) - Number(b.number);
        });

        return _qrcodes;
    }

    private LoadServiceForm(){
        let _serviceid = this._service?.name || null;
        let _tables = this._POSQrCodes;

        if (this._serviceForm == null){
            this._serviceForm = new FormGroup({
                serviceid: new FormControl(_serviceid, [
                    Validators.required,
                    this.checkAvailableService.bind(this)                
                ]),
                tables: new FormControl(_tables, [
                    // no validators
                ])
            });
        }
        else {
            this._serviceForm.patchValue({
                serviceid: _serviceid,
                tables: _tables
            });
        }

        this._serviceForm.markAsPristine();
        setTimeout(() => {
            this._serviceForm.markAsPristine();
        }, 0);

        this.change.markForCheck();
    }

    /*****************************/
    /* SERVICE STATUS            */
    /*****************************/

    private _check_interval = null;
    private _check_ticksecs = 0;    // check every 5 seconds

    get SvcCheck(){
        let _hassvc = this._service.connected.some(
        _connect => {
            return _connect.hassvc;
        });

        return _hassvc;
    }

    get DevCount(){
        return this._service.connected.length || 0;
    }

    private _wsversion = null;
    get WSVersion(){
        return this._wsversion;
    }

    private async _CheckStatus(){
        if (!this.SvcCheck){
            return false;
        }

        let _connection = await this.devsvc.Test(null, null, this.ServiceForm.get('serviceid').value, false);
        this._service.IsOnline = !!_connection;
        if (_connection){
            this._wsversion = _connection.version;
        }
    }

    get ServiceStatus() {
        if (!this.SvcCheck){
            return 'SERVICE';
        }

        return (this._service.IsOnline) ? 'ONLINE' : 'OFFLINE';
    }

    RunCheck(){
        if (this._check_interval == null){
            this._check_interval = this.sync.Clock.OnOneSecondTick.subscribe(
            data => {
                this._check_ticksecs = (this._check_ticksecs + 1) % 5;
                if (this._active && (this._check_ticksecs == 0)){  // do not check if not active
                    this._CheckStatus();
                }
            });
        }

        return this._service.IsOnline;
    }

    /*****************************/
    /* DATA VALIDATION           */
    /*****************************/

    private get IsValid(){
        if (!this.ServiceForm.valid){
            return false;
        } 

        if (this.SvcCheck){
            if (!this._devicesctrl || !this._devicesctrl.IsValid){
                return false;
            }    
        }

        return true;
    }

    private _isdirty = false;
    private get IsDirty(){
        let _isdirty = (this._isdirty || (this.ServiceForm.dirty) );
        if (!this.IsValid){
            return false;   // do not consider if its not valid
        }

        _isdirty = _isdirty || (this._devicesctrl && this._devicesctrl.IsDirty)

        return _isdirty;
    }

    private set IsDirty(value){
        this._isdirty = value;
        if (value == false){
            this.ServiceForm.markAsPristine();
        }

        if (this._devicesctrl){
            this._devicesctrl.IsDirty = false;
        }
    }

    get CanAcceptChanges(){
        return this.IsValid && this.ServiceForm.valid && this.IsDirty;
    }

    /****************************/
    /* STARTS HERE              */
    /****************************/

    private _service_subscription = null;

    constructor(private lang: languageService, private change: ChangeDetectorRef, private alertCtrl: alertService, private devsvc: deviceService, private sync: syncService, private data: dataService, public view: viewService) {
        super(lang, change);
    }

    ngOnInit(){
        this._CheckStatus();    // perform first check
    }

    ngOnChanges(){
        if (this._service_subscription){
            this._service_subscription.unsubscribe();
            this._service_subscription = null;
        }

        this._service_subscription = this._service.OnRefresh.subscribe(
        data => {
            if (this.ServiceForm.pristine){
                this.LoadServiceForm();
            }

            this.change.markForCheck();
        });
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._service_subscription){
            this._service_subscription.unsubscribe();
            this._service_subscription = null;
        }

        if (this._check_interval){
            this._check_interval.unsubscribe();
            this._check_interval = null;
        }        
    }

    private _ApplyTables(_service, tables){
        // include all qrcodes in the final list
        for(let _qrcode of tables){
            _qrcode.till = _service;
            _qrcode.DoRefresh('QRCODE');
        }

        // remove qrcodes not in the final list
        for(let _qrcode of _service.tables){
            if (!(tables.includes(_qrcode))){
                _qrcode.till = null;
                _qrcode.DoRefresh('QRCODE');
            }
        }
    }

    private ApplyChanges(){
        this._service.name = this.ServiceForm.get('serviceid').value;
        this._ApplyTables(this._service, this.ServiceForm.get('tables').value);

        if (this._devicesctrl){
            this._devicesctrl.ApplyChanges();
        }

        this.IsDirty = false;        
    }

    private DiscardChanges(){
        this.LoadServiceForm();

        if (this._devicesctrl){
            this._devicesctrl.DiscardChanges();
        }
    }

    /****************************/
    /* EVENT HANDLERS           */
    /****************************/

    get EditDevice(){
        return this._devicesctrl?.EditDevice;
    }

    set EditDevice(value){
        if (this._devicesctrl){
            this._devicesctrl.EditDevice = value;
        }
    }

    @Output('OnCancel') _onCancel = new EventEmitter<any>();     
    private _OnCancel(){
        this.DiscardChanges();
        this._onCancel.emit();
    }

    async CancelChanges(){
        if (this.IsDirty){
            console.log("[EDIT RASPPI] confirm changes lost");
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@changes_lost_confirm_tittle'),
                message: this.lang.tr('@changes_lost_confirm_message'),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);

                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);
                        return false;
                    }
                }]
            });

            alert.onDidDismiss().then((detail:any) => {
                if (detail.data == true){
                    this._OnCancel();
                }
            });

            await alert.present();
        }
        else {
            this._OnCancel();
        }
    }

    @Output('OnChange') _onChange = new EventEmitter<any>();   
    AcceptChanges(){
        this.ApplyChanges();
        this._onChange.emit();
    }

    async OnServiceRemove(){
        let _canremove = true;
        
        if (this._service.connect){
            _canremove = (this._service.connect.device != this.data.session.deviceid)
        }

        if (_canremove) {
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@place_till_delete_confirm'),
                message: this.lang.tr('@place_till_delete_message', [ this._service.name || this.lang.tr('@place_till_noname') ]),
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);
                        return false;
                    }
                }]
            });
    
            alert.onDidDismiss().then(async (detail:any) => {
                if (detail.data == true){
                    // remove all the connections
                    for(let _connect of this._service.connected){
                        _connect.status = 'DE';
                    }
    
                    // remove the selected service
                    this._service.status = 'DE';
                    
                    this.IsDirty = false;
                    this._onChange.emit();
                }
            });
    
            await alert.present();    
        }

        else {
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@place_till_delete_error'),
                message: this.lang.tr('@place_till_delete_thispos'),
                buttons: [{
                    text: this.lang.tr('@ok'),
                    handler: () => {
                        alert.dismiss(true);                    
                    }
                }]
            });
    
            alert.onDidDismiss().then(
            data => {
                setTimeout(() => {
                    alert.dismiss(true);
                }, 100);

                return false;
            });
    
            await alert.present();             
        }
    }

    /****************************/
    /* MANAGED TABLES           */
    /****************************/

    private _selectedtables = null;
    get SelectTables(){
        return this._selectedtables;
    }

    OnShowTables(){
        this._selectedtables = this.ServiceForm.get('tables').value || [];;
        this.change.markForCheck();
    }

    OnAddTables(tables){
        this.OnHideTables();

        tables.sort((a, b) => {
            return a.number - b.number;
        });

        this.ServiceForm.get('tables').setValue(tables);
        this.ServiceForm.markAsDirty();

        this.change.markForCheck();     
    }

    OnHideTables(){
        this._selectedtables = null;
        this.change.markForCheck();
    }

    /****************************/
    /* CONNECTED DEVICES        */
    /****************************/

    @ViewChild('devices', { static: false }) _devicesctrl: UiRaspPIDevices;
    get SelectProducts(){
        if (this._devicesctrl) {
            return this._devicesctrl.DeviceProducts;
        }

        return null;
    }

    OnAddProducts(products){
        if (this._devicesctrl){
            this._devicesctrl.OnAddProducts(products);
        }

        this.change.markForCheck();
    }

    OnHideProducts(){
        if (this._devicesctrl){
            this._devicesctrl.OnHideProducts();
        }

        this.change.markForCheck();
    }    
}